import { useCallback, useState } from 'react';
import useSWRMutation from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import { getRequestErrorMessage } from '@/lib/api/get-request-error-message';
import { type NetworkType } from '@mls-wallet/shared-types';
import { type AccountProposal } from '../account/use-account-proposals';

type CreateAccountNetworkProposalInput = {
  accountId: string;
  networkIds: NetworkType[];
};

interface UseCreateAccountNetworkProposalReturn {
  createNetworkProposal: (
    input: CreateAccountNetworkProposalInput
  ) => Promise<AccountProposal>;
  isCreating: boolean;
  createError: string | null;
  clearCreateError: () => void;
}

const createNetworkProposalFetcher = async (
  _key: string,
  { arg }: { arg: CreateAccountNetworkProposalInput }
): Promise<AccountProposal> => {
  // Creates a normal account ADD proposal that is linked to an existing account
  // by the account id in the path. The backend derives org/user context from auth.
  return await denApi.post<AccountProposal, { networkIds: NetworkType[] }>(
    `admin/multichain-account/${encodeURIComponent(arg.accountId)}/network-proposals`,
    { networkIds: arg.networkIds }
  );
};

function getCreateNetworkProposalErrorMessage(error: unknown): string {
  const message = getRequestErrorMessage(error, 'Failed to create proposal');

  switch (message) {
    case 'network_already_added':
      return 'This account already has one of the selected networks.';
    case 'network_add_proposal_already_pending':
      return 'A proposal to add one of these networks is already pending.';
    default:
      return message;
  }
}

/**
 * Mutation hook for proposing additional networks on an existing account.
 *
 * The proposal still goes through the normal account-review/signing flow; this
 * hook only creates the pending proposal and leaves execution to the review UI.
 */
export function useCreateAccountNetworkProposal(): UseCreateAccountNetworkProposalReturn {
  const [createError, setCreateError] = useState<string | null>(null);

  const { trigger: triggerCreateNetworkProposal, isMutating: isCreating } =
    useSWRMutation<
      AccountProposal,
      Error,
      string,
      CreateAccountNetworkProposalInput
    >('create-account-network-proposal', createNetworkProposalFetcher);

  const createNetworkProposal = async (
    input: CreateAccountNetworkProposalInput
  ): Promise<AccountProposal> => {
    setCreateError(null);

    try {
      return await triggerCreateNetworkProposal(input);
    } catch (err) {
      setCreateError(getCreateNetworkProposalErrorMessage(err));
      throw err;
    }
  };
  const clearCreateError = useCallback(() => setCreateError(null), []);

  return {
    createNetworkProposal,
    isCreating,
    createError,
    clearCreateError,
  };
}
